// Sound effects utility using Web Audio API
// Generates sounds programmatically - no external files needed

class GameSounds {
  constructor() {
    this.audioContext = null;
    this.enabled = true;
  }

  // Initialize audio context (must be called after user interaction)
  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.audioContext;
  }

  // Enable/disable sounds
  setEnabled(enabled) {
    this.enabled = enabled;
  }

  // Play a tone with envelope
  playTone(frequency, duration, type = 'sine', volume = 0.3) {
    if (!this.enabled) return;
    
    const ctx = this.init();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;

    // Envelope for smooth sound
    const now = ctx.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(volume, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration);

    oscillator.start(now);
    oscillator.stop(now + duration);
  }

  // Correct answer - quick happy chime
  playCorrect() {
    if (!this.enabled) return;
    this.playTone(880, 0.1, 'sine', 0.2);  // A5
    setTimeout(() => this.playTone(1108, 0.15, 'sine', 0.2), 80);  // C#6
  }

  // Wrong answer - low buzz
  playWrong() {
    if (!this.enabled) return;
    this.playTone(200, 0.2, 'square', 0.15);
    setTimeout(() => this.playTone(150, 0.3, 'square', 0.1), 100);
  }

  // X-Ray Vision unlock - magical shimmer
  playXRayVision() {
    if (!this.enabled) return;
    const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6 - arpeggio
    notes.forEach((freq, i) => {
      setTimeout(() => this.playTone(freq, 0.3, 'sine', 0.2), i * 80);
    });
  }

  // 7-in-a-row bonus - triumphant fanfare
  playStreakBonus() {
    if (!this.enabled) return;
    const ctx = this.init();
    
    // Play a power-up style ascending sound
    const notes = [392, 494, 587, 784]; // G4, B4, D5, G5
    notes.forEach((freq, i) => {
      setTimeout(() => {
        this.playTone(freq, 0.2, 'square', 0.15);
        this.playTone(freq * 1.5, 0.2, 'sine', 0.1); // Harmony
      }, i * 100);
    });
    
    // Final chord
    setTimeout(() => {
      this.playTone(784, 0.4, 'sine', 0.2);  // G5
      this.playTone(988, 0.4, 'sine', 0.15); // B5
      this.playTone(1175, 0.4, 'sine', 0.1); // D6
    }, 450);
  }

  // Game over - descending tones
  playGameOver() {
    if (!this.enabled) return;
    const notes = [440, 392, 349, 294]; // A4, G4, F4, D4
    notes.forEach((freq, i) => {
      setTimeout(() => this.playTone(freq, 0.3, 'sine', 0.2), i * 150);
    });
  }

  // Victory - triumphant melody
  playVictory() {
    if (!this.enabled) return;
    const melody = [
      { freq: 523, dur: 0.15 },  // C5
      { freq: 523, dur: 0.15 },  // C5
      { freq: 523, dur: 0.15 },  // C5
      { freq: 698, dur: 0.4 },   // F5
      { freq: 659, dur: 0.15 },  // E5
      { freq: 659, dur: 0.15 },  // E5
      { freq: 659, dur: 0.15 },  // E5
      { freq: 784, dur: 0.5 },   // G5
    ];
    
    let time = 0;
    melody.forEach(({ freq, dur }) => {
      setTimeout(() => this.playTone(freq, dur, 'sine', 0.25), time);
      time += dur * 800;
    });
  }

  // Milestone achieved - celebratory ping
  playMilestone() {
    if (!this.enabled) return;
    this.playTone(880, 0.1, 'sine', 0.2);
    setTimeout(() => this.playTone(1175, 0.1, 'sine', 0.2), 100);
    setTimeout(() => this.playTone(1397, 0.2, 'sine', 0.25), 200);
  }
}

// Singleton instance
const gameSounds = new GameSounds();

export default gameSounds;
